import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import {
  EventTypes,
  ILengthValidation,
  IMaskValidation,
  InputValue,
  Validations,
} from "../interfaces";

import { defaultErr, verifyErr } from "./const";

@Component
export default class Input extends Vue {
  val = "";
  unmaskedValue = "";
  input: HTMLInputElement = this.$refs.input as HTMLInputElement;
  valid = false;
  isDisabled = false;

  mouseDown = false;
  touched = false;
  focus = false;

  validationErrors: Validations[] = [];
  validationError = "";
  persistentError?: boolean = undefined;
  errors: (string | undefined)[] = [];

  @Prop({ type: String, default: "" })
  readonly name!: string;

  @Prop({ type: String, default: "text" })
  readonly type!: string;

  @Prop({ type: String, default: "" })
  readonly autocomplete!: string;

  @Prop({ type: String, default: "Заполните это поле" })
  readonly placeholder!: string;

  @Prop({ type: Boolean, default: false })
  readonly required!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly disabled!: boolean;

  @Prop({ type: String, default: defaultErr })
  readonly errorMessage!: string;

  @Prop({ type: Function, default: () => null })
  readonly setRef!: (name: string, value?: Vue) => void;

  @Prop({ type: Function, default: () => null })
  readonly returnValue!: (
    name: string,
    val: InputValue,
    type: EventTypes,
  ) => void;

  @Prop({ type: Function, default: () => null })
  readonly init!: (name: string, init: () => Array<string | undefined>) => void;

  @Prop({ type: String })
  readonly initial?: string;

  @Prop({ type: String })
  readonly prefix?: string;

  @Prop({ type: String })
  readonly mode?: string;

  @Prop({ type: [String, Number] })
  readonly verify?: string | number;

  @Prop({ type: Object })
  readonly mask?: IMaskValidation;

  @Prop({ type: Object })
  readonly regex?: IMaskValidation;

  @Prop({ type: Object })
  readonly max?: ILengthValidation;

  @Prop({ type: Object })
  readonly min?: ILengthValidation;

  get isPhone() {
    return this.name === "phone";
  }

  get empty() {
    return !this.val || this.prefix === this.val;
  }

  created() {
    this.init(this.name, this.forceErrorCheck);
  }

  mounted() {
    this.input = this.$refs.input as HTMLInputElement;
    this.valid = false;
    this.isDisabled = false;
    this.setRef(this.name, this);

    if (this.initial) {
      const value = this.unmasked(this.initial);
      this.val = this.unmaskedValue = value;
    }

    setTimeout(() => {
      this.mountedUtility();
      if (this.initial) this.mountedNextStack();
    }, 0);
  }

  @Watch("val")
  valueWatcher(n: string) {
    this.unmaskedValue = this.unmasked(n);
  }

  @Watch("disabled")
  disabledWatcher(n: boolean) {
    if (n) {
      this.clear();
    }
  }

  @Watch("errorMessage")
  errorMessageWatcher(n: string | string[]) {
    if (n !== defaultErr) {
      if (typeof n === "string") {
        this.setError(n);
      } else if (n.length) {
        this.setError(n.join(", "));
      }
    }
  }

  @Watch("verify")
  public clear() {
    this.val = "";
    this.input.value = "";
    this.valid = false;
    this.validationError = "";
    this.returnValue(this.name, null, EventTypes.AUTO);
  }

  clearString(str: string, sub: string): string {
    const newString = str.replace(sub, "");
    return newString !== str ? this.clearString(newString, sub) : newString;
  }

  public unmasked(value: string) {
    if (this.mask && typeof this.mask.value === "string") {
      const mask = this.mask.value;
      const chars = mask.split("");
      const values = value.split("");
      const unmasked = chars
        .map((maskChar: string, index: number) => {
          return maskChar !== values[index] ? values[index] : "";
        })
        .join("");
      return unmasked;
    }
    return value;
  }

  public setError(msg: string, persistent?: boolean) {
    this.validationError = msg;
    this.persistentError = persistent;
    if (this.touched) this.valid = !msg;
  }

  inputHandler(e: any) {
    this.val = e.target.value;

    if (
      this.mask &&
      e.target.value.length > this.mask.value.length &&
      typeof this.mask.value === "string"
    ) {
      return;
    }

    if (e.isTrusted) {
      this.touched = true;
      this.validationError = "";
    }

    if (this.prefix) {
      if (e.inputType && e.inputType === "insertFromPaste") {
        const cleared =
          this.prefix + this.clearString(e.target.value, this.prefix);
        this.val = cleared;
      }
      if (e.target.value.length <= this.prefix.length) {
        if (e.data && e.data.length) {
          this.val = this.prefix + e.data;
        } else {
          this.val = this.prefix;
        }
      }
    }

    setTimeout(() => {
      this.inputUtility();
      this.inputNextStack();
    }, 0);
  }

  inputUtility() {}

  mountedUtility() {}

  mountedNextStack() {
    this.checkForValid();
    this.checkForErrors();
    this.returnValue(this.name, this.initial as InputValue, EventTypes.AUTO);
  }

  inputNextStack() {
    this.checkForValid();
    this.returnValue(
      this.name,
      this.valid ? this.unmaskedValue : null,
      EventTypes.MANUAL,
    );
  }

  keyDownHandler(e: any) {
    if (this.mouseDown) e.preventDefault();
  }

  keyHandler(e: any) {
    if (e.ctrlKey && e.key === "z") {
      this.forceErrorCheck();
    }
    this.cursorReset(this.input);
  }

  focusHandler() {
    if (this.valid) this.input.select();
    if (!this.val && this.prefix) this.val = this.prefix;
    this.focus = true;
    this.$emit("focus");
  }

  mouseDownHandler() {
    this.mouseDown = true;
    if (!this.val && this.prefix) this.val = this.prefix;
    this.cursorReset(this.input);
  }

  mouseUpHandler(e: any) {
    this.mouseDown = false;
    this.cursorReset(e.target);
  }

  blurHandler() {
    this.focus = false;
    this.checkForErrors();
    if (this.touched) {
      setTimeout(() => {
        this.checkForErrors();
        this.$emit("blur");
      }, 0);
    }
  }

  cursorReset(input: HTMLInputElement) {
    if (this.prefix) {
      const cursorSelection = input.selectionStart === input.selectionEnd;
      const emptySelected = input.selectionStart
        ? input.selectionStart <= this.prefix.length
        : true;
      if (cursorSelection && emptySelected) {
        input.selectionStart = input.selectionEnd = this.prefix.length;
      }
    }
  }

  maskIsFilled(value = ""): boolean {
    if (this.mask) {
      let maskLength = this.mask.value.length;
      if (typeof this.mask.value === "function") {
        maskLength = (this.mask.value as any)(value).length;
      }
      return value.length >= maskLength;
    } else {
      return false;
    }
  }

  public checkForValid() {
    this.validationErrors = [];
    let valid = true;

    if (this.required && !this.unmaskedValue) {
      valid = false;
      this.validationErrors.push(Validations.required);
    }
    if (this.verify) {
      valid = this.unmaskedValue === this.verify;
      if (!valid) this.validationErrors.push(Validations.verify);
    }
    if (this.min) {
      valid = this.unmaskedValue.length >= this.min.value;
      if (!valid) this.validationErrors.push(Validations.min);
    }
    if (this.max) {
      valid = this.unmaskedValue.length <= this.max.value;
      if (!valid) this.validationErrors.push(Validations.max);
    }
    if (this.regex && this.val) {
      const reg = new RegExp(this.regex.value, "i");
      valid = reg.test(this.val);
      if (!valid) this.validationErrors.push(Validations.regex);
    }
    if (this.mask) {
      valid = this.maskIsFilled(this.val) || this.empty;
      if (!valid) this.validationErrors.push(Validations.mask);
    }
    this.valid = !this.validationErrors.length;
  }

  public checkForErrors() {
    const errors = this.validationErrors.map((error: Validations) => {
      if (error === Validations.verify) {
        return verifyErr;
      } else if (error === Validations.required) {
        return this.errorMessage || defaultErr;
      } else {
        return this[error]?.errorMessage;
      }
    });

    if (errors.length) {
      this.errors = errors;
      if (errors[0]) this.setError(errors[0]);
      return true;
    } else {
      if (!this.persistentError) {
        this.errors = [];
        this.setError("");
      }
      return false;
    }
  }

  forceErrorCheck() {
    this.checkForValid();
    this.checkForErrors();
    this.returnValue(
      this.name,
      this.valid ? this.unmaskedValue : null,
      EventTypes.AUTO,
    );
    return this.errors;
  }
}
